import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Container, Grid, Paper, Chip, TextField,
  Accordion, AccordionSummary, AccordionDetails, Alert, Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  MovieFilter as DCPIcon,
  Shield as ShieldIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
  Send as SendIcon,
  CollectionsBookmark as CampaignIcon,
  Palette as KeyArtIcon,
  Usb as PhysicalIcon,
  Groups as TeamIcon,
  Chat as CommentIcon,
  Insights as AnalyticsIcon,
  Lock as EncryptionIcon,
  Theaters as VenueIcon,
} from '@mui/icons-material';

const GOLD = '#d4a017';
const BG_DARK = '#0a0c10';
const BG_CARD = '#12151c';
const BG_SECTION = '#0e1017';
const TEXT_PRIMARY = '#e8e6e3';
const TEXT_SECONDARY = 'rgba(232,230,227,0.55)';
const BORDER = 'rgba(212,160,23,0.12)';

function LandingPage() {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  // Setter is called when the contact form submits; value isn't rendered
  // because the snackbar already shows the success state. Kept for a
  // future "Thanks — we'll reply within 24h" inline confirmation.
  // eslint-disable-next-line no-unused-vars
  const [_contactSent, setContactSent] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [signupAllowed, setSignupAllowed] = useState(false);

  useEffect(() => {
    api
      .get('/api/auth/signup-allowed')
      .then((res) => setSignupAllowed(!!res.data?.allowed))
      .catch(() => setSignupAllowed(false));
  }, []);

  const handleContact = (e) => {
    e.preventDefault();
    // For now, mailto fallback
    const subject = encodeURIComponent('Screening Room Inquiry');
    const body = encodeURIComponent(`Name: ${contactForm.name}\nEmail: ${contactForm.email}\n\n${contactForm.message}`);
    window.location.href = `mailto:screeners@hollywoodhills.media?subject=${subject}&body=${body}`;
    setContactSent(true);
    setSnackbar({ open: true, message: 'Opening your email client...' });
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const Bullet = ({ children }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
      <CheckIcon sx={{ color: GOLD, fontSize: 18, mt: 0.3, flexShrink: 0 }} />
      <Typography variant="body2" sx={{ color: TEXT_PRIMARY, lineHeight: 1.6 }}>{children}</Typography>
    </Box>
  );

  const SectionTitle = ({ children, subtitle }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: '-0.02em', mb: subtitle ? 1.5 : 0 }}>
        {children}
      </Typography>
      {subtitle && <Typography variant="body1" sx={{ color: TEXT_SECONDARY, maxWidth: 720, lineHeight: 1.7 }}>{subtitle}</Typography>}
    </Box>
  );

  const FeatureCard = ({ icon, title, children }) => (
    <Paper sx={{
      p: 3, bgcolor: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 3,
      height: '100%', display: 'flex', flexDirection: 'column',
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 1.5,
        bgcolor: 'rgba(212,160,23,0.1)', border: `1px solid ${BORDER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mb: 2,
      }}>
        {React.cloneElement(icon, { sx: { color: GOLD, fontSize: 22 } })}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: TEXT_SECONDARY, lineHeight: 1.6, flex: 1 }}>{children}</Typography>
    </Paper>
  );

  return (
    <Box sx={{ bgcolor: BG_DARK, color: TEXT_PRIMARY, minHeight: '100vh' }}>
      {/* ─── NAV ─── */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 100,
        bgcolor: 'rgba(10,12,16,0.92)', backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${BORDER}`,
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <DCPIcon sx={{ color: GOLD, fontSize: 28 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.1, letterSpacing: '-0.01em' }}>Screening Room</Typography>
                <Typography sx={{ fontSize: '0.55rem', color: GOLD, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>by HHMG</Typography>
              </Box>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3 }}>
              {[
                ['Campaigns', 'campaigns'],
                ['DCPs', 'product-dcp'],
                ['Screeners', 'product-screeners'],
                ['Key Art', 'product-keyart'],
                ['Pricing', 'pricing'],
                ['FAQ', 'faq'],
              ].map(([label, id]) => (
                <Typography key={id} onClick={() => scrollTo(id)} sx={{
                  fontSize: '0.85rem', color: TEXT_SECONDARY, cursor: 'pointer',
                  '&:hover': { color: GOLD }, transition: 'color 0.2s',
                }}>{label}</Typography>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button size="small" onClick={() => navigate('/login')} sx={{ color: TEXT_SECONDARY, fontSize: '0.85rem', textTransform: 'none' }}>Sign In</Button>
              {signupAllowed && (
                <Button size="small" variant="contained" onClick={() => navigate('/register')} sx={{
                  bgcolor: GOLD, color: BG_DARK, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem',
                  '&:hover': { bgcolor: '#b8860b' },
                }}>Create Account</Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ─── HERO ─── */}
      <Box sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 6, md: 10 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="overline" sx={{ color: GOLD, letterSpacing: 2, fontWeight: 700, fontSize: '0.75rem' }}>
            One platform. One campaign per film. Every audience.
          </Typography>
          <Typography variant="h2" sx={{
            fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3.2rem' },
            mt: 2, mb: 3,
          }}>
            Take your film from master to every screen, screener, and stakeholder.
          </Typography>
          <Typography variant="h6" sx={{
            color: TEXT_SECONDARY, fontWeight: 400, lineHeight: 1.6,
            fontSize: { xs: '1rem', md: '1.15rem' },
            maxWidth: 720, mx: 'auto', mb: 5,
          }}>
            Theatrical DCPs with automated QC, encrypted delivery, and KDMs.
            Per-recipient watermarked screeners with streaming, downloads, and recipient comments back to the filmmaker.
            Key art bundles for press and exhibitors. Physical drive delivery when the network won't do.
            All organized under one Campaign per film, all sent to the right people automatically.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
            {signupAllowed && (
              <Button size="large" variant="contained" onClick={() => navigate('/register')} sx={{
                bgcolor: GOLD, color: BG_DARK, fontWeight: 700, px: 4, py: 1.5, fontSize: '1rem',
                textTransform: 'none', '&:hover': { bgcolor: '#b8860b' },
              }}>
                Create an account
              </Button>
            )}
            <Button size="large" variant="outlined" onClick={() => scrollTo('contact')} sx={{
              borderColor: 'rgba(212,160,23,0.4)', color: GOLD, fontWeight: 600, px: 4, py: 1.5,
              fontSize: '1rem', textTransform: 'none', '&:hover': { borderColor: GOLD, bgcolor: 'rgba(212,160,23,0.06)' },
            }}>
              Talk to us
            </Button>
          </Box>
          <Typography onClick={() => scrollTo('pricing')} sx={{
            color: TEXT_SECONDARY, fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline',
            textDecorationColor: 'rgba(232,230,227,0.25)', '&:hover': { color: GOLD },
          }}>
            See pricing
          </Typography>
        </Container>
      </Box>

      {/* ─── TRUST STRIP ─── */}
      <Box sx={{ bgcolor: BG_SECTION, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, py: 3 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {[
              'SDR 2K + 4K DCPs',
              'Automated source + DCP + KDM QC',
              'Encrypted delivery + KDMs',
              'Per-recipient watermarked screeners',
              'Streaming + downloads + comments',
              'Physical drive delivery + FedEx',
              'Venue playback verification',
            ].map((item) => (
              <Grid key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2 }}>
                <CheckIcon sx={{ color: GOLD, fontSize: 16 }} />
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{item}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── CAMPAIGNS ─── (the org-frame, leads the page) */}
      <Box id="campaigns" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="overline" sx={{ color: GOLD, letterSpacing: 2, fontWeight: 700 }}>
              The organizing primitive
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, mb: 2, letterSpacing: '-0.02em' }}>
              One Campaign per film, every activity under one timeline
            </Typography>
            <Typography sx={{ color: TEXT_SECONDARY, maxWidth: 720, mx: 'auto', lineHeight: 1.7 }}>
              No more spreadsheets cross-referencing which press got which cut, which venue confirmed which KDM,
              which buyer got the screener two weeks ago. Every DCP, every screener, every KDM, every key art bundle,
              every venue outcome threads into the film's Campaign. The producer signs in and sees the whole picture.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FeatureCard icon={<CampaignIcon />} title="The Plex of your film">
                A picker that looks more like browsing your movie library than navigating a CMS. Posters tile cleanly,
                resize without jitter, and a single campaign auto-opens so you skip the chooser. Pick your film and
                land on its dashboard.
              </FeatureCard>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FeatureCard icon={<AnalyticsIcon />} title="Producer-facing rollup">
                Total DCP deliveries (across theaters), emergency MP4 fallbacks, screeners watched, average watch
                completion, comments back from press — every number on the dashboard belongs to this film. Drill in to
                the per-recipient lifecycle when the question gets specific.
              </FeatureCard>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FeatureCard icon={<KeyArtIcon />} title="Marketing assets bundled in">
                Upload your key art (PSDs, posters, banners, EPK, social cuts) once. The platform extracts dimensions,
                generates previews, and lets you bundle a multi-select share for press in one click — a zip with a
                14-day download link, or a direct attachment when it fits.
              </FeatureCard>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FeatureCard icon={<TeamIcon />} title="The right people, always">
                Producer, director, booking agent, publicist — add them once to your team and tick "CC on every email."
                They ride along automatically on every delivery, KDM, share, and reminder the system sends on the
                film's behalf. The operator never has to remember to loop them in.
              </FeatureCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── FORMAT SUPPORT CALLOUT ─── */}
      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 6 }, pb: 1 }}>
        <Paper sx={{ p: { xs: 3, md: 4 }, bgcolor: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 3 }}>
          <Typography variant="overline" sx={{ color: GOLD, letterSpacing: 1.5, fontWeight: 700 }}>
            Self-service scope
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
            Built for the formats most films actually ship in
          </Typography>
          <Typography variant="body2" sx={{ color: TEXT_SECONDARY, mb: 3, lineHeight: 1.6, maxWidth: 720 }}>
            The vast majority of theatrical releases (festival runs, indie distribution, four-walled
            screenings, awards qualifying) finish in SDR with stereo or 5.1 audio. That's the lane
            we automate end-to-end so you can self-serve a finished DCP without waiting on a vendor.
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ color: GOLD, mb: 1 }}>What you can ship today, on your own</Typography>
              <Bullet>SDR (Rec.709) at 2K and 4K resolution</Bullet>
              <Bullet>Stereo (2.0) and 5.1 surround audio</Bullet>
              <Bullet>SMPTE DCP, encrypted or unencrypted</Bullet>
              <Bullet>KDM generation for theatrical playback</Bullet>
              <Bullet>Per-recipient watermarked screeners</Bullet>
              <Bullet>HLS streaming player with watch analytics</Bullet>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ color: GOLD, mb: 1 }}>For everything else, talk to us</Typography>
              <Bullet>HDR / Dolby Vision finishes</Bullet>
              <Bullet>Immersive audio (Atmos, 7.1 + height)</Bullet>
              <Bullet>IMF / IMAX / unusual aspect masters</Bullet>
              <Bullet>VFX-heavy reels needing a hand-tuned encode</Bullet>
              <Bullet>Festival cuts with embedded captioning + DCDM</Bullet>
              <Bullet>Bulk distribution to 100+ venues</Bullet>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* ─── QC CALLOUT ─── */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto' }}>
          <ShieldIcon sx={{ color: GOLD, fontSize: 36, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5, letterSpacing: '-0.02em' }}>
            Every source, every DCP, every KDM checked automatically
          </Typography>
          <Typography sx={{ color: TEXT_SECONDARY, mb: 3, lineHeight: 1.7 }}>
            QC isn't a separate vendor pass after the fact — it's wired into every step. The browser sniffs your
            source before the first byte uploads (HEVC long-GOP, VFR, no video stream — all rejected up front).
            240+ structural conformance checks (Clairmeta + DCP-o-matic verify) run on every DCP build before the
            tar even starts. Every KDM gets inspected the moment it's issued. The QC report ships with the
            deliverable so downstream gets verification metadata for free.
          </Typography>
          <Typography sx={{ color: TEXT_SECONDARY, lineHeight: 1.7 }}>
            You see a compliance badge before you ever start an encode, with a "what to fix" hint when something
            won't pass. The platform refuses to build a DCP from a source that can't pass projector ingest, so
            you never ship a file that gets rejected at the theater.
          </Typography>
        </Box>
      </Container>

      {/* ─── PRODUCT INTRO ─── */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 4, bgcolor: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 3, height: '100%' }}>
              <Typography variant="overline" sx={{ color: GOLD, letterSpacing: 1.5, fontWeight: 700 }}>Theatrical DCP</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 1, mb: 2 }}>
                Self-service DCP creation + encrypted delivery
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_SECONDARY, mb: 3, lineHeight: 1.7 }}>
                Upload your master, choose your format, and let the platform build a SMPTE DCP with automatic QC,
                encryption, KDMs, and theater delivery. We can also coordinate with the venue end-to-end when you
                want playback confirmation before showtime.
              </Typography>
              <Button endIcon={<ArrowIcon />} onClick={() => scrollTo('product-dcp')} sx={{ color: GOLD, textTransform: 'none', fontWeight: 600 }}>
                More about DCPs
              </Button>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 4, bgcolor: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 3, height: '100%' }}>
              <Typography variant="overline" sx={{ color: GOLD, letterSpacing: 1.5, fontWeight: 700 }}>Watermarked Screeners</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 1, mb: 2 }}>
                Per-recipient screeners + streaming + comments back
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_SECONDARY, mb: 3, lineHeight: 1.7 }}>
                A uniquely-watermarked encode per recipient — if it leaks, you know whose copy it was. Recipients
                stream in-browser (no download required) or download a watermarked file. They can leave comments
                back to the filmmaker. You see every open, every play, every viewing session in real time.
              </Typography>
              <Button endIcon={<ArrowIcon />} onClick={() => scrollTo('product-screeners')} sx={{ color: GOLD, textTransform: 'none', fontWeight: 600 }}>
                More about screeners
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* ─── DCP DEEP DIVE ─── */}
      <Box id="product-dcp" sx={{ bgcolor: BG_SECTION, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <SectionTitle subtitle="Upload your master, choose your format, and generate a SMPTE DCP through a guided workflow. Resolution and frame rate auto-derive from your source; encryption is one checkbox; ISDCF naming is automatic. Built for the team that wants a faster path to delivery without giving up professional options.">
            Create your DCP online
          </SectionTitle>
          <Bullet>Automated source pre-flight (codec, container, FFEC/FFMC detection, BS.1770 loudness)</Bullet>
          <Bullet>SMPTE DCP build with auto-derived reel splits + ISDCF content type</Bullet>
          <Bullet>240+ Clairmeta conformance checks + DCP-o-matic verify on every build</Bullet>
          <Bullet>Encryption + KDM generation on the same page</Bullet>
          <Bullet>QC report ships inside the DCP tarball — downstream gets verification metadata for free</Bullet>
          <Box sx={{ mt: 3 }}>
            {signupAllowed && (
              <Button variant="contained" onClick={() => navigate('/register')} sx={{
                bgcolor: GOLD, color: BG_DARK, fontWeight: 700, textTransform: 'none', mr: 1,
                '&:hover': { bgcolor: '#b8860b' },
              }}>
                Start your DCP
              </Button>
            )}
            <Button variant="outlined" onClick={() => scrollTo('contact')} sx={{
              borderColor: 'rgba(212,160,23,0.4)', color: GOLD, textTransform: 'none', fontWeight: 600,
              '&:hover': { borderColor: GOLD, bgcolor: 'rgba(212,160,23,0.06)' },
            }}>
              Talk to a producer
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ─── KDM + ENCRYPTION ─── */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <SectionTitle subtitle="Add encryption to your package and issue KDMs with a simple workflow. The validity window auto-derives from the venue's submitted screening dates so you don't accidentally email a KDM that doesn't unlock at showtime — the system explicitly refuses to issue a KDM whose window doesn't cover the booking.">
          Encryption and KDMs
        </SectionTitle>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Bullet>Single + bulk KDM generation off the distribution page</Bullet>
            <Bullet>Validity window driven by the venue's actual submitted dates</Bullet>
            <Bullet>KDM files served as <code>.kdm</code> (the ext modern Dolby IMS3000 expects)</Bullet>
            <Bullet>Opt-in email attachment for known-good recipients; link fallback by default for filter-stripped paths</Bullet>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Bullet>Validation against the booking automatically blocks misaligned windows</Bullet>
            <Bullet>Tokenized landing page per recipient (no recipient login needed)</Bullet>
            <Bullet>Per-recipient resend + revoke + audit log</Bullet>
            <Bullet>Cert request reminders to venues without certs — weekly far out, every other day inside two weeks, at a reasonable hour in the venue's timezone</Bullet>
          </Grid>
        </Grid>
      </Container>

      {/* ─── VENUE CONFIRMATION ─── */}
      <Box sx={{ bgcolor: BG_SECTION, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <SectionTitle subtitle="For releases that need more confidence, we coordinate directly with the venue to confirm the auditorium, projector, playback server, certificate details, and playback readiness. We send a short encrypted test file and confirm the screen is ready before showtime. Outcomes flow back into the venue's profile so the next operator sees what worked.">
            Need the theater confirmed? We handle that too.
          </SectionTitle>
          <Bullet>Venue outreach + tech-contact collection via tokenized request form</Bullet>
          <Bullet>Multi-auditorium submission — venues add several screens + certs in one go</Bullet>
          <Bullet>Test file delivery + playback confirmation before your screening date</Bullet>
          <Bullet>Observation-driven outcome history per venue — "last attempt used .zip; needed unencrypted MP4 fallback" surfaces at the next delivery</Bullet>
        </Container>
      </Box>

      {/* ─── SCREENERS DEEP DIVE ─── */}
      <Container id="product-screeners" maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <SectionTitle subtitle="Create per-recipient watermarked screeners by re-encoding your content with each recipient's name and identifier baked into the picture. Send a uniquely-marked encode to each press contact, awards voter, sales prospect, or internal reviewer. If it leaks, you know whose copy it was.">
          Secure watermarked screeners
        </SectionTitle>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" sx={{ color: GOLD, mb: 1 }}>Delivery</Typography>
            <Bullet>Per-recipient unique watermark (name + email visible on picture)</Bullet>
            <Bullet>Multi-recipient batch — one click sends N uniquely-watermarked encodes</Bullet>
            <Bullet>HLS streaming player (no download required for screening)</Bullet>
            <Bullet>Optional download with per-recipient share link + tracked download events</Bullet>
            <Bullet>Per-job link expiration; revoke any share instantly</Bullet>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" sx={{ color: GOLD, mb: 1 }}>Visibility back to the filmmaker</Typography>
            <Bullet>Recipient lifecycle — Sent → Opened → NDA Accepted → Streaming → Watched %</Bullet>
            <Bullet>Comments to Filmmakers — recipients leave notes the producer reads in-app</Bullet>
            <Bullet>Live "Active viewers" beacon when someone is currently watching</Bullet>
            <Bullet>Follow-Viewer operator preview — see exactly where a recipient is in the cut</Bullet>
            <Bullet>Per-share transfer telemetry (bandwidth, completion, rebuffering, device)</Bullet>
          </Grid>
        </Grid>
      </Container>

      {/* ─── KEY ART ─── */}
      <Box id="product-keyart" sx={{ bgcolor: BG_SECTION, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <KeyArtIcon sx={{ color: GOLD, fontSize: 36 }} />
            <Typography variant="overline" sx={{ color: GOLD, letterSpacing: 1.5, fontWeight: 700 }}>Marketing assets</Typography>
          </Box>
          <SectionTitle subtitle="Upload your campaign's key art — posters, banners, social cuts, PSDs, even oversized layered files — and let the platform extract dimensions, generate previews, and bundle whatever the recipient needs. Send press a zip with the EPK. Send a distributor a single high-res poster. Set the campaign's hero image from any uploaded image with one click.">
            Key art that ships at the same speed as the film
          </SectionTitle>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Bullet>Ingest from zip, tar, individual files — formats from JPG and PNG through layered PSB and InDesign</Bullet>
              <Bullet>Auto-preview generation (Pillow, pypdfium2 for PDFs, ffmpeg for video sizzles)</Bullet>
              <Bullet>Operator-uploaded preview override for files our pipeline can't render (2 GB layered PSBs, InDesign sources)</Bullet>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Bullet>Multi-select share — single file ships direct, multi ships as a server-built zip</Bullet>
              <Bullet>Email attach with a 15 MB cap, decided up front from source-file sizes (no waiting for a zip to fail)</Bullet>
              <Bullet>Per-download tracking — see when press opens the link, from where, on what device</Bullet>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── PHYSICAL DELIVERY ─── */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <PhysicalIcon sx={{ color: GOLD, fontSize: 36 }} />
          <Typography variant="overline" sx={{ color: GOLD, letterSpacing: 1.5, fontWeight: 700 }}>Physical delivery</Typography>
        </Box>
        <SectionTitle subtitle="When a venue can't accept a digital handoff — a 5G feature over a flaky theater LAN, an IMAX master, a remote drive-in — we ship a physical USB drive with FedEx pickup. The system writes the DCP, prints the label, and tracks the drive end-to-end through delivery, ingest test, and confirmed screening.">
          When bandwidth isn't enough
        </SectionTitle>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Bullet>USB-drive staging directly from the DCP — no zip round-trip, projector-ready perms baked in</Bullet>
            <Bullet>Per-drive unique filesystem label (e.g. <code>HHMG-X7K2RH</code>) so two drives are never visually identical</Bullet>
            <Bullet>FedEx Ship API integration: label printed, tracking number stored, status auto-flips on delivery</Bullet>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Bullet>Three signature events tracked: signed-for at delivery, received at venue, tested in-projector</Bullet>
            <Bullet>Multi-venue stacking — one drive carries DCPs for an entire route</Bullet>
            <Bullet>Pivot-to-physical action revokes the digital share + records the outcome atomically</Bullet>
          </Grid>
        </Grid>
      </Container>

      {/* ─── TEAM + COMMS ─── */}
      <Box sx={{ bgcolor: BG_SECTION, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TeamIcon sx={{ color: GOLD, fontSize: 36 }} />
            <Typography variant="overline" sx={{ color: GOLD, letterSpacing: 1.5, fontWeight: 700 }}>Team + communications</Typography>
          </Box>
          <SectionTitle subtitle="Add your team once — producer, director, booking agent, publicist, attorney, post supervisor, marketing lead, family rep, anyone — and the right person sees every email automatically. The producer gets a copy of every delivery. The booking agent sees every KDM. The publicist follows every key art share. Without you remembering to CC them.">
            The right people, on every email
          </SectionTitle>
          <Bullet>Role-grouped contact roster — Production, Talent, Distribution, Post, Marketing, Operations, Filmmaker, Other</Bullet>
          <Bullet>~30 curated roles in the dropdown, plus a free-text "Other" for unusual titles</Bullet>
          <Bullet>Multiple contacts per role — assistant producer + line producer + EP all CC'd together</Bullet>
          <Bullet>Tick "CC by default" on a contact and they ride along on every outgoing email forever</Bullet>
          <Bullet>Operator's explicit per-email CC always wins; defaults augment + dedupe</Bullet>
        </Container>
      </Box>

      {/* ─── PRICING ─── */}
      <Box id="pricing" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>Simple pricing, with support where it counts</Typography>
            <Typography sx={{ color: TEXT_SECONDARY }}>Start with self-service DCP creation and add only the services you need.</Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {[
              {
                title: 'DCP Encode + QC',
                price: 'Starting at $795',
                sub: '2K feature-length',
                items: ['2K and 4K options', 'Automated QC included', 'Hosted delivery', 'One correction round'],
              },
              {
                title: 'Encryption + KDMs',
                price: 'From $95',
                sub: 'per title',
                items: ['Encryption setup', 'First 5 KDMs included', 'Additional KDMs from $10', 'Rush options available'],
              },
              {
                title: 'Playback Confirmation',
                price: 'From $195',
                sub: 'per screen',
                items: ['Venue coordination', 'Test file delivery', 'Playback confirmation', 'One correction cycle'],
              },
              {
                title: 'Watermarked Screeners',
                price: 'Starting at $95',
                sub: 'short-form',
                items: ['Per-recipient watermarks', 'Streaming + downloads', 'Comments back to filmmaker', 'Features from $495'],
              },
              {
                title: 'Key Art Hosting + Share',
                price: 'Included',
                sub: 'with any title',
                items: ['Unlimited assets per campaign', 'Auto-previewed + bundled', 'Press-ready share links', 'Download analytics'],
              },
              {
                title: 'Physical Drive Delivery',
                price: 'From $145',
                sub: 'per drive + shipping',
                items: ['Drive + format + stage', 'FedEx Ship API', 'Full tracking + POD', 'Multi-venue route stacking'],
              },
            ].map((card) => (
              <Grid key={card.title} size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper sx={{
                  p: 3, bgcolor: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 3,
                  height: '100%', display: 'flex', flexDirection: 'column',
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{card.title}</Typography>
                  <Typography sx={{ color: GOLD, fontWeight: 700, fontSize: '1.3rem', mb: 0.25 }}>{card.price}</Typography>
                  <Typography variant="caption" sx={{ color: TEXT_SECONDARY, mb: 2.5 }}>{card.sub}</Typography>
                  <Box sx={{ flex: 1 }}>
                    {card.items.map((item) => (
                      <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CheckIcon sx={{ color: GOLD, fontSize: 14 }} />
                        <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: '0.8rem' }}>{item}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="outlined" onClick={() => scrollTo('contact')} sx={{
              borderColor: 'rgba(212,160,23,0.4)', color: GOLD, textTransform: 'none', fontWeight: 600,
              '&:hover': { borderColor: GOLD, bgcolor: 'rgba(212,160,23,0.06)' },
            }}>
              See full pricing
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ─── WHO IT'S FOR ─── */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 4 }}>Built for teams releasing real films</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', mb: 4 }}>
          {['Independent filmmakers', 'Producers', 'Distributors', 'Sales teams', 'Festivals', 'Post houses', 'Estates + archives'].map((who) => (
            <Chip key={who} label={who} sx={{
              bgcolor: 'rgba(212,160,23,0.08)', color: TEXT_PRIMARY, border: `1px solid ${BORDER}`,
              fontSize: '0.85rem', fontWeight: 500, px: 1,
            }} />
          ))}
        </Box>
        <Typography sx={{ color: TEXT_SECONDARY, maxWidth: 640, mx: 'auto', lineHeight: 1.7 }}>
          Whether you need a quick self-service DCP, a secure screener with comments back from press, a key art
          bundle to send a buyer, or a release workflow with multi-venue physical delivery — the platform scales
          from a one-day awards submission to a multi-quarter theatrical campaign.
        </Typography>
      </Container>

      {/* ─── HOW IT WORKS ─── */}
      <Box id="how-it-works" sx={{ bgcolor: BG_SECTION, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 5, textAlign: 'center' }}>How it works</Typography>
          <Grid container spacing={4}>
            {[
              { step: '1', title: 'Create a campaign', desc: 'One campaign per film. Add your team — producer, booking agent, publicist — so the right people get every email.' },
              { step: '2', title: 'Upload your master', desc: 'Source pre-flight catches HEVC long-GOP, VFR, FFEC/FFMC issues before the first byte uploads.' },
              { step: '3', title: 'Pick a workflow', desc: 'Self-service DCP, encrypted release, watermarked screener, or coordinated venue confirmation.' },
              { step: '4', title: 'Deliver', desc: 'Tokenized links per recipient, streaming + download, KDMs scheduled to actual screening dates, FedEx physical handoff when needed.' },
              { step: '5', title: 'See everything', desc: 'Watch sessions, recipient comments, venue outcomes, delivery confirmations — all under the campaign timeline.' },
              { step: '6', title: 'Release with confidence', desc: 'The audit log catches the last 1% of mistakes. The QC report ships with every asset. The producer always knows what shipped.' },
            ].map((s) => (
              <Grid key={s.step} size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                    bgcolor: 'rgba(212,160,23,0.1)', border: `1px solid ${BORDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Typography sx={{ color: GOLD, fontWeight: 700 }}>{s.step}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>{s.title}</Typography>
                    <Typography variant="body2" sx={{ color: TEXT_SECONDARY, lineHeight: 1.6 }}>{s.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── FAQ ─── */}
      <Container id="faq" maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>Frequently asked questions</Typography>
        {[
          ['What source files do you accept?', 'Most professional video formats: ProRes, DNxHR, H.264, H.265, MXF. The browser sniffs your source before uploading; HEVC long-GOP, VFR, no-video-stream, and missing-audio files are rejected up front in under 5 seconds with zero bandwidth wasted. Your source should be the highest quality master you have.'],
          ['Do you support 2K and 4K DCPs?', 'Yes. Both 2K and 4K DCP creation are available. Resolution and frame rate auto-derive from your source; reel splits are auto-generated for features. ISDCF naming is automatic from the campaign metadata.'],
          ['Can I add encryption later?', 'Yes. You can create an unencrypted DCP first and add encryption as a separate step, or enable encryption from the start. KDMs are issued from the same distribution page.'],
          ['How are KDMs priced?', 'The first 5 KDMs are included with encryption setup ($95). Additional standard KDMs are $10 each, with batch pricing at $7 each for 10+. Rush and after-hours KDMs are available at $35 each.'],
          ['What does Playback Confirmation actually do?', 'We contact the venue, confirm the auditorium, projector, server, and certificate details, send a test DCP, issue a test KDM, and confirm successful playback before your screening date. Outcomes are logged into the venue\'s profile so the next operator sees what worked.'],
          ['How do watermarked screeners work?', 'Each recipient gets a uniquely-watermarked encode with their name and email visible on the picture. They can stream in-browser through our HLS player or download a watermarked file. You see every open, every play, and recipient comments back to the filmmaker in real time.'],
          ['Can recipients leave feedback for the filmmaker?', 'Yes — Comments to Filmmakers is built into the watch player. The recipient drops in a timestamped note, the producer sees it on the campaign dashboard with a "Has comments" filter. No third-party tool, no separate login.'],
          ['What\'s a Campaign?', 'One Campaign per film. Every DCP, every screener, every KDM, every key art share, every venue outcome attaches to the campaign. The producer signs in and sees one timeline — total deliveries, screeners watched, comments back, what shipped where. No spreadsheets.'],
          ['Can you ship a physical drive?', 'Yes. For venues that can\'t take a digital handoff (5G features over flaky theater LANs, IMAX masters, remote drive-ins), we stage a USB drive directly from the DCP, label it with a unique HHMG-XXXXXX code, generate a FedEx label through their Ship API, and track the drive end-to-end through delivery, ingest test, and confirmed screening.'],
          ['Who gets emails about my film?', 'You add your team once — producer, booking agent, publicist, attorney, anyone — and pick the roles. Tick "CC by default" on the contacts who should see everything; they ride along on every delivery, KDM, key art share, and reminder automatically. The operator never has to remember.'],
          ['Do you offer rush turnaround?', 'Yes. 2-business-day rush is +35%, next-business-day is +60%, and same-day (if accepted) is +100% over standard pricing.'],
          ['Do I need an account before starting?', 'Yes, creating a free account takes about 30 seconds. Once signed in, you create a Campaign for your film and the workspace opens up.'],
        ].map(([q, a]) => (
          <Accordion key={q} disableGutters sx={{
            bgcolor: 'transparent', borderBottom: `1px solid ${BORDER}`,
            '&:before': { display: 'none' }, boxShadow: 'none',
          }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: TEXT_SECONDARY }} />}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ color: TEXT_SECONDARY, lineHeight: 1.7 }}>{a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>

      {/* ─── CONTACT ─── */}
      <Box id="contact" sx={{ bgcolor: BG_SECTION, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="sm">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5, textAlign: 'center' }}>Talk to us</Typography>
          <Typography sx={{ color: TEXT_SECONDARY, textAlign: 'center', mb: 4 }}>
            Questions about pricing, your release, or how the platform fits your workflow? We're happy to help.
          </Typography>
          <Paper component="form" onSubmit={handleContact} sx={{ p: 4, bgcolor: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 3 }}>
            <TextField label="Name" fullWidth required value={contactForm.name}
              onChange={(e) => setContactForm(f => ({ ...f, name: e.target.value }))}
              sx={{ mb: 2 }} />
            <TextField label="Email" type="email" fullWidth required value={contactForm.email}
              onChange={(e) => setContactForm(f => ({ ...f, email: e.target.value }))}
              sx={{ mb: 2 }} />
            <TextField label="Tell us about your project" multiline rows={4} fullWidth value={contactForm.message}
              onChange={(e) => setContactForm(f => ({ ...f, message: e.target.value }))}
              sx={{ mb: 3 }} />
            <Button type="submit" variant="contained" fullWidth startIcon={<SendIcon />} sx={{
              bgcolor: GOLD, color: BG_DARK, fontWeight: 700, py: 1.5, textTransform: 'none', fontSize: '1rem',
              '&:hover': { bgcolor: '#b8860b' },
            }}>
              Send message
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* ─── FINAL CTA ─── */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          One campaign. Every audience. One platform.
        </Typography>
        <Typography sx={{ color: TEXT_SECONDARY, maxWidth: 640, mx: 'auto', mb: 4, lineHeight: 1.7 }}>
          Start a Campaign, add your team, and ship your film — from the master to every screener, every theater,
          every press contact, every distributor — without losing track of what went where.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {signupAllowed && (
            <Button size="large" variant="contained" onClick={() => navigate('/register')} sx={{
              bgcolor: GOLD, color: BG_DARK, fontWeight: 700, px: 4, py: 1.5, textTransform: 'none', fontSize: '1rem',
              '&:hover': { bgcolor: '#b8860b' },
            }}>
              Create an account
            </Button>
          )}
          <Button size="large" variant="outlined" onClick={() => scrollTo('contact')} sx={{
            borderColor: 'rgba(212,160,23,0.4)', color: GOLD, fontWeight: 600, px: 4, py: 1.5,
            textTransform: 'none', fontSize: '1rem', '&:hover': { borderColor: GOLD, bgcolor: 'rgba(212,160,23,0.06)' },
          }}>
            Talk to us
          </Button>
        </Box>
      </Container>

      {/* ─── FOOTER ─── */}
      <Box sx={{ borderTop: `1px solid ${BORDER}`, py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DCPIcon sx={{ color: GOLD, fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>Screening Room</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
              All rights reserved {new Date().getFullYear()} Hollywood Hills Media Group
            </Typography>
            <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
              <a href="mailto:screeners@hollywoodhills.media" style={{ color: 'inherit', textDecoration: 'none' }}>
                screeners@hollywoodhills.media
              </a>
            </Typography>
          </Box>
        </Container>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default LandingPage;
